import React, { useState } from "react";
import PagesHeader from "@components/PagesHeader";
import SectionHeader from "@components/SectionHeader";
import BrandLogo from "../../components/BrandLogo";
import StickyButton from "@components/StickyButton";
import Button from "@components/Button";
import NiceModal from "@ebay/nice-modal-react";
import UnderlineButton from "@components/UnderlineButton";
import {
  PRIZE_CACHE_TIME,
  PRIZE_STALE_TIME,
  getPrizeDescribe,
} from "../../api/prize";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import {
  getModuleValue,
  parseCMSBold,
  parseHTMLToText,
  useWindowSize,
  fetchData,
} from "../../Helpers";
import "./style.scss";
import {
  DETAILS_CACHE_TIME,
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "../../api/consumer";
import DOMPurify from "dompurify";
import WarningIcon from "@assets/warning.svg?react";

export default function RedeemAwardPage() {
  const { width } = useWindowSize();
  const { uuid } = useParams();
  const [loading, setLoading] = useState(0);
  const queryClient = useQueryClient();

  function formatDate(timestamp) {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const { data, refetch: refetchData } = useQuery({
    queryKey: ["prize-describe", uuid],
    queryFn: () => getPrizeDescribe(uuid),
    staleTime: PRIZE_STALE_TIME,
    cacheTime: PRIZE_CACHE_TIME,
  });

  const detailsQuery = useQuery({
    queryKey: ["user-details"],
    queryFn: () => getUserDetails(),
    refetchInterval: DETAILS_REFETCH_INVERVAL,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  const catalogue = data?.catalogue;
  const modules = catalogue?.content?.modules;

  const peroidConfig = {
    DAY: "Tag",
    WEEK: "Woche",
    MONTH: "Monat",
  };

  const logosModules = getModuleValue(
    modules,
    "online_redeem",
    "metadata.logos",
  );

  function redeemButtonCopy() {
    if (catalogue) {
      if (!catalogue.inStock) {
        return "Nicht lieferbar";
      } else if (!catalogue.hasLevel) {
        return "Status unpassend";
      } else if (!catalogue.hasPoints) {
        return "Mehr Punkte sammeln";
      } else if (catalogue.periodicLimitExceeded) {
        return "Einlöselimit erreicht";
      } else {
        return "Gutschein abholen";
      }
    }
  }

  function redeemButtonLock() {
    if (!catalogue) {
      return true;
    }
    return (
      !catalogue.inStock ||
      !catalogue.hasLevel ||
      !catalogue.hasPoints ||
      catalogue.limitExceeded ||
      catalogue.periodicLimitExceeded ||
      !catalogue.available
    );
  }

  function redeemAward() {
    fetchData({
      endpoint: "/prize/redeem",
      payload: {
        prize: {
          uuid: DOMPurify.sanitize(uuid),
        },
      },
      successCallback: (resObj) => {
        setLoading(0);
        refetchData();
        detailsQuery.refetch();
        queryClient.invalidateQueries({ queryKey: ["prize-list"] });

        NiceModal.show("redeem-award-modal", {
          awardName: parseCMSBold(catalogue?.content?.description ?? ""),
          ident: uuid,
          bg: getModuleValue(modules, "thumbnail", "file.url"),
          imgDesc: getModuleValue(modules, "thumbnail", "metadata.imgDesc"),
          awardUUID: resObj.data[0].uuid,
          brands: catalogue?.brands,
          logos: logosModules,
        });
      },
      errorCallback: () => {
        setLoading(0);
        detailsQuery.refetch();
        queryClient.invalidateQueries({ queryKey: ["prize-list"] });
      },
      setLoading,
    });
  }

  return (
    <section className="redeem-award-page">
      <PagesHeader
        back
        dangerouslySetInnerHTML={parseCMSBold(
          catalogue?.content?.description ?? "",
        )}
      />
      <div className="desktop-wrapper">
        {width >= 1020 && (
          <div className="award-img-desktop">
            <img
              src={getModuleValue(modules, "hero_image", "file.url")}
              alt={getModuleValue(modules, "hero_image", "metadata.imgDesc")}
            />
            <BrandLogo brands={catalogue?.brands} />
            {getModuleValue(modules, "text", "custom") && (
              <div className="warning-box">
                <div className="warning-icon">
                  <WarningIcon />
                </div>
                <div
                  className="warning-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(getModuleValue(modules, "text", "custom")),
                  }}
                ></div>
              </div>
            )}
          </div>
        )}
        <div className="main-content">
          {width < 1020 && (
            <>
              <div className="award-img-mobile">
                <img
                  src={getModuleValue(modules, "thumbnail", "file.url")}
                  alt={getModuleValue(modules, "thumbnail", "metadata.imgDesc")}
                />
                <BrandLogo brands={catalogue?.brands} />
              </div>
              {getModuleValue(modules, "text", "custom") && (
                <div className="warning-box">
                  <div className="warning-icon">
                    <WarningIcon />
                  </div>
                  <div
                    className="warning-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(getModuleValue(modules, "text", "custom")),
                    }}
                  ></div>
                </div>
              )}
            </>
          )}
          <div className="award-summary">
            <SectionHeader>
              <strong>Einzelheiten</strong> des Angebots
            </SectionHeader>
            <div
              className="content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(getModuleValue(
                  modules,
                  "online_redeem",
                  "metadata.about",
                )),
              }}
            />
            <p>
              <span>KOSTET:</span> {catalogue?.price} PUNKTE
            </p>
            <p>
              <span>Ablaufdatum:</span> {formatDate(catalogue?.expirationDate)}
            </p>
            {catalogue?.period !== null && (
              <p>
                <span>Einlöselimit:</span> 1 pro{" "}
                {peroidConfig[catalogue?.period]}
              </p>
            )}
            {width >= 1020 && (
              <Button
                action={() => {
                  redeemAward();
                }}
                disabled={redeemButtonLock()}
              >
                {redeemButtonCopy()}
              </Button>
            )}
          </div>

          {logosModules?.length > 0 && (
            <div className="award-info">
              <SectionHeader>
                <strong>Zur Verwendung auf:</strong>
              </SectionHeader>
              <div className="logos">
                {logosModules.map((e, index) => (
                  <img src={e.file.url} alt={e.alt} key={index} />
                ))}
              </div>
            </div>
          )}

          {parseHTMLToText(
            getModuleValue(modules, "online_redeem", "metadata.howToUse"),
            "li",
            true,
          )?.length !== 0 && (
            <UnderlineButton
              className="how-to-use"
              onClick={() =>
                NiceModal.show("howToUse-modal", {
                  howToUse: parseHTMLToText(
                    getModuleValue(
                      modules,
                      "online_redeem",
                      "metadata.howToUse",
                    ),
                    "li",
                    true,
                  ),
                })
              }
            >
              Anwendungstipps
            </UnderlineButton>
          )}
        </div>
      </div>
      {width < 1020 && (
        <StickyButton
          action={() => {
            redeemAward();
          }}
          disabled={redeemButtonLock()}
        >
          {redeemButtonCopy()}
        </StickyButton>
      )}
    </section>
  );
}
